import React, { useEffect, useMemo, useState } from "react";
import "./Products.css";
import { useProduct } from "../../context/ProductContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faCartPlus,
  faTag,
  faEye,
  faArrowRightArrowLeft,
  faCircleCheck,
  faMagnifyingGlass,
  faBolt,
  faLock,
  faHeadset,
} from "@fortawesome/free-solid-svg-icons";
import NortonBanner from "../NortonBanner/NortonBanner";
import WhyChooseUs from "../WhyChooseUs/WhyChooseUs";

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { products, setProductToShow, cart, setCart } = useProduct();

  // tabs
  const BRAND_TABS = useMemo(() => {
    const keys = Object.keys(products || {});
    const stable = ["Norton", "Kaspersky", "McAfee", "Avast"];
    const rest = keys.filter((k) => !stable.includes(k)).sort();
    const ordered = [...stable.filter((s) => keys.includes(s)), ...rest];
    return ["All", ...ordered]; // All first (better UX)
  }, [products]);

  const [activeTab, setActiveTab] = useState("All");
  const [sortBy, setSortBy] = useState("recommended"); // recommended | alpha | priceLow | priceHigh
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Helpers
  const formatINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(n || 0));

  const short = (s = "", words = 24) => {
    const parts = String(s).trim().split(/\s+/);
    return parts.slice(0, words).join(" ") + (parts.length > words ? "…" : "");
  };

  const normalize = (s = "") => String(s).toLowerCase().trim();

  // flatten all products (preserve brand key)
  const allProducts = useMemo(() => {
    const obj = products || {};
    return Object.keys(obj).flatMap((brandKey) =>
      (obj[brandKey] || []).map((p) => ({
        ...p,
        brand: p.brand || brandKey,
      }))
    );
  }, [products]);

  // filter by tab
  const filteredByTab = useMemo(() => {
    if (activeTab === "All") return allProducts;
    return allProducts.filter((p) => normalize(p.brand) === normalize(activeTab));
  }, [allProducts, activeTab]);

  // search filter
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return filteredByTab;
    return filteredByTab.filter((p) => {
      const hay = `${p.name || ""} ${p.brand || ""} ${p.desc || ""} ${p.license || ""}`;
      return normalize(hay).includes(q);
    });
  }, [filteredByTab, query]);

  // recommended score (simple heuristic)
  const score = (p) => {
    const price = Number(p.price || 0);
    const mrp = Number(p.mrp || 0);
    const hasMrp = mrp > price && mrp > 0;
    const off = hasMrp ? Math.round(((mrp - price) / mrp) * 100) : 0;

    // slight boost if has bigger discount + mid-range price
    let s = 0;
    s += Math.min(off, 60); // discount weight
    s += price > 0 ? Math.max(0, 40 - Math.abs(price - 599) / 20) : 0; // sweet-spot
    if ((p.name || "").toLowerCase().includes("vpn")) s += 10;
    if ((p.name || "").toLowerCase().includes("total")) s += 8;
    return s;
  };

  // sorting
  const sorted = useMemo(() => {
    const list = [...filtered];

    if (sortBy === "priceLow") {
      list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === "priceHigh") {
      list.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortBy === "alpha") {
      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    } else {
      // recommended
      list.sort((a, b) => score(b) - score(a));
    }

    return list;
  }, [filtered, sortBy]);

  const totalCount = sorted.length;

  // Navigation + cart
  const handleNavigation = (item) => {
    setProductToShow(item);
    const slugBase = String(item.name || "").toLowerCase();

    navigate(
      `/${
        slugBase.includes("norton")
          ? "norton"
          : slugBase.includes("mcafee")
          ? "mcafee"
          : slugBase.includes("avast")
          ? "avast"
          : slugBase.includes("kaspersky")
          ? "kaspersky"
          : "security"
      }/${slugBase.replace(/\s+/g, "-")}`
    );
  };

  const handleCart = (item) => {
    if (!cart.some((c) => c.id === item.id)) {
      setCart((prev) => [...prev, item]);
      toast.success(`${item.name} added to cart`);
    } else {
      toast.info(`${item.name} is already in your cart`);
    }
  };

  // UI pieces
  const PriceBlock = ({ price, mrp }) => {
    const p = Number(price || 0);
    const m = Number(mrp || 0);
    const hasMrp = m > p && m > 0;
    const off = hasMrp ? Math.round(((m - p) / m) * 100) : 0;

    return (
      <div className="px-price">
        <div className="px-price-main">
          <span className="px-now">{formatINR(p)}</span>
          <span className="px-term">/ year</span>
        </div>
        {hasMrp ? (
          <div className="px-price-meta">
            <span className="px-mrp">{formatINR(m)}</span>
            <span className="px-off">
              <FontAwesomeIcon icon={faTag} /> Save {off}%
            </span>
          </div>
        ) : (
          <div className="px-price-meta">
            <span className="px-meta-soft">Instant delivery • Easy renewals</span>
          </div>
        )}
      </div>
    );
  };

  const InfoBadges = ({ license }) => (
    <div className="px-badges">
      <span className="px-badge">
        <FontAwesomeIcon icon={faCircleCheck} /> Instant email delivery
      </span>
      <span className="px-badge">
        <FontAwesomeIcon icon={faLock} /> Genuine license
      </span>
      <span className="px-badge subtle">
        <FontAwesomeIcon icon={faHeadset} /> Setup help
      </span>
      {license && (
        <span className="px-badge soft">
          <FontAwesomeIcon icon={faTag} /> {license}
        </span>
      )}
    </div>
  );

  const Card = ({ item, index }) => {
    const inCart = cart.some((c) => c.id === item.id);
    const p = Number(item.price || 0);
    const m = Number(item.mrp || 0);
    const hasMrp = m > p && m > 0;
    const off = hasMrp ? Math.round(((m - p) / m) * 100) : 0;

    const isBestValue = sortBy === "recommended" && index === 0 && totalCount >= 3;

    return (
      <article className={`px-card ${isBestValue ? "best" : ""}`}>
        <div className="px-topline">
          <span className="px-brand">{item.brand || "Security"}</span>
          {isBestValue ? (
            <span className="px-burst">
              <FontAwesomeIcon icon={faBolt} /> Best value
            </span>
          ) : off >= 35 ? (
            <span className="px-deal">
              <FontAwesomeIcon icon={faTag} /> Hot deal
            </span>
          ) : null}
        </div>

        <button className="px-thumb" onClick={() => handleNavigation(item)} aria-label={`Open ${item.name}`}>
          <img src={item.img} alt={item.name} loading="lazy" />
        </button>

        <div className="px-body">
          <h3 className="px-title" onClick={() => handleNavigation(item)} role="link" tabIndex={0}>
            {item.name}
          </h3>

          <p className="px-desc">
            {short(
              item.desc ||
                "Everyday protection for browsing, banking and downloads — plus help to activate and set it up correctly.",
              26
            )}
          </p>

          <InfoBadges license={item.license} />

          <div className="px-foot">
            <PriceBlock price={item.price} mrp={item.mrp} />

            <div className="px-actions">
              <button className="px-mini" onClick={() => handleNavigation(item)}>
                <FontAwesomeIcon icon={faEye} /> Details
              </button>

              <button
                className={`px-btn ${inCart ? "incart" : ""}`}
                onClick={() => handleCart(item)}
              >
                {inCart ? (
                  "✔ Added"
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCartPlus} /> Add
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section
      className="products-v2"
      style={{ marginTop: location.pathname === "/latest-products" ? "8rem" : "0" }}
    >
      {/* HERO / FILTERS */}
      <header className="px-hero" role="region" aria-label="Product filters">
        <div className="px-hero-inner">
          <div className="px-copy">
            <div className="px-eyebrow">
              <FontAwesomeIcon icon={faShieldAlt} />
              <span>Pick a plan in 60 seconds</span>
            </div>

            <h2 className="px-h2">
              Security plans with setup help — made for real people
            </h2>

            <p className="px-sub">
              Choose from trusted brands, compare pricing, and get{" "}
              <strong>instant license delivery</strong>. Need help activating?
              Our team guides you step-by-step.
            </p>

            <div className="px-stats">
              <span className="px-stat">
                Showing <strong>{totalCount}</strong> plan{totalCount === 1 ? "" : "s"}
              </span>
              <span className="px-dot">•</span>
              <span className="px-stat">
                Brand: <strong>{activeTab}</strong>
              </span>
              <span className="px-dot">•</span>
              <span className="px-stat">
                Sort: <strong>{sortBy === "recommended" ? "Recommended" : sortBy}</strong>
              </span>
            </div>
          </div>

          <div className="px-panel">
            {/* Search */}
            <div className="px-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by plan name, brand, VPN, devices…"
                aria-label="Search products"
              />
            </div>

            {/* Tabs */}
            <nav className="px-tabs" role="tablist" aria-label="Filter by brand">
              {BRAND_TABS.map((t) => (
                <button
                  key={t}
                  className={`px-tab ${activeTab === t ? "active" : ""}`}
                  role="tab"
                  aria-selected={activeTab === t}
                  onClick={() => setActiveTab(t)}
                  type="button"
                >
                  {t}
                </button>
              ))}
            </nav>

            {/* Sort */}
            <div className="px-sort">
              <label htmlFor="sort" className="px-sort-label">
                <FontAwesomeIcon icon={faArrowRightArrowLeft} /> Sort
              </label>
              <select
                id="sort"
                className="px-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recommended">Recommended</option>
                <option value="alpha">Name: A–Z</option>
                <option value="priceLow">Price: Low to High</option>
                <option value="priceHigh">Price: High to Low</option>
              </select>
            </div>

            <p className="px-tip">
              Tip: Start with “Recommended” to see best value plans first.
            </p>
          </div>
        </div>
      </header>

      {/* GRID */}
      <div className="px-wrap">
        <div className="px-grid" role="list">
          {sorted.map((item, idx) => (
            <Card key={item.id} item={item} index={idx} />
          ))}
        </div>

        {sorted.length === 0 && (
          <div className="px-empty">
            No plans match your filters. Try another brand or clear the search.
          </div>
        )}
      </div>

      {/* Optional sections */}
      <NortonBanner />
      <WhyChooseUs />
    </section>
  );
};

export default Products;
